import type { Language } from '@gig/magic-sdk/dist/adapter/users/language';

import { config } from '../../config';
import { locale } from './zendesk.utils';
import { isMobileQuery, languageQuery } from '@gig/magic-common';

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        zE: any;
        zESettings: any;
        $zopim: any;
    }
}

export class ZendeskLegacy {
    /**
     * Functions executed when the initial chat loading begins.
     */
    private listeners = new Set<() => any>();

    /**
     * Indicates if the chat has been loaded (script added to DOM, etc.).
     */
    isLoaded: boolean = false;

    /**
     * Indicates if loading above is in the process.
     */
    isLoading: boolean = false;

    /**
     * A label for the currently selected option (a.k.a. "Choose a language").
     */
    getSelectedOption(language: Language): string {
        return (
            config.zendesk?.labels[language] ||
            config.zendesk?.labels[config.zendesk.defaultLanguage] ||
            ''
        );
    }

    /**
     * Sets Zendesk settings: languages, current language, and the tag (brand
     * name).
     */
    setZendeskSettings(language: Language): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            window.zE?.(() => {
                window.$zopim(async () => {
                    if (await isMobileQuery()) {
                        window.$zopim.livechat.button.setOffsetVerticalMobile(
                            '60',
                        );
                    }
                    this.isLoaded = true;
                    resolve(true);
                });

                window.zE('webWidget', 'updateSettings', {
                    webWidget: {
                        chat: {
                            departments: {
                                enabled: config.zendesk?.languages.map(
                                    (lang) => config.zendesk?.labels[lang],
                                ),
                                select: this.getSelectedOption(language),
                                tags: [`${config.magic.brand}`],
                            },
                        },
                    },
                });
            });
        });
    }

    /**
     * Sets Zendesk's language to the current language.
     */
    setLanguage(language: Language): void {
        window.zE?.('webWidget', 'setLocale', locale(language));
    }

    /**
     * Adds a listener for close event for a widget which hides it in these
     * circumstances.
     */
    registerOnCloseZendesk(): void {
        window.zE?.('webWidget:on', 'close', () => {
            window.zE?.('webWidget', 'hide');
        });
    }

    /**
     * Hides Zendesk: the widget is removed from the view (no button, nothing).
     */
    hideZendesk(): void {
        window.zE?.hide();
    }

    /**
     * Shows Zendesk.
     */
    showZendesk(): void {
        window.zE?.('webWidget', 'show');
    }

    /**
     * Opens Zendesk.
     */
    openZendesk(): void {
        window.zE?.('webWidget', 'open');
    }

    // On that note, I have no idea what is the difference between show and
    // open, and the Zendesk's documentation is as helpful.

    /**
     * Lazily loads Zendesk's script and returns a promise that is resolved
     * when Zendesk's iframe is added to DOM.
     */
    loadZendesk() {
        return new Promise<void>((resolve, reject) => {
            const script = document.createElement(
                'script',
            ) as HTMLScriptElement;
            script.id = 'ze-snippet';
            script.src = config?.zendesk?.sdkUrl as string;
            script.onload = () => {
                const checker = setInterval(() => {
                    const iframe = document.querySelector(
                        'iframe[data-product="web_widget"]',
                    ) as HTMLIFrameElement | null;

                    if (iframe) {
                        clearInterval(checker);
                        resolve();
                    }
                }, 4);
            };
            script.onerror = () => reject();
            document.body.appendChild(script);
        });
    }

    /**
     * A function that starts Zendesk and if it's not loaded, initiates lazy
     * loading. It also registers onClose handler.
     */
    async startingZendesk(): Promise<void> {
        if (this.isLoading) {
            return;
        }

        const language = await languageQuery();

        if (!window.zE) {
            try {
                this.isLoading = true;

                for (const listener of this.listeners) {
                    listener();
                }

                await this.loadZendesk();
                await this.setZendeskSettings(language);
                this.registerOnCloseZendesk();
            } catch (error) {
                console.error('Error loading Zendesk');
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        }

        this.setLanguage(language);
        this.openZendesk();
        this.showZendesk();
    }

    /**
     * Adds a listener to be fired when script loading starts.
     * @param fn A callback.
     * @returns function A function to remove the listener from the list.
     */
    onLoadingStart(fn: () => any): () => void {
        this.listeners.add(fn);

        return () => {
            this.listeners.delete(fn);
        };
    }
}
